import { isShopOfferSettings, shopOfferResultMatches, type ShopOfferSettings, type ShopOfferBody } from "./shopOfferModel";
import { isOfferHistory, offerHistoryCursorLast, type OfferHistoryPage } from "./offerHistoryModel";
import { isCaseHistoryPage } from "./caseHistoryModel";
import { isCaseOrderCheck, type CaseOrderCheck } from "./caseOrderCheckModel";
import { isPolicyHistory } from "./policyHistoryModel";
import { isCaseAssignmentResult, isCaseAssignmentHistoryPage, validCaseAssignment, type CaseAssignmentBody } from "./caseAssignmentModel";
import { isCaseAssigneesPage, isCaseTransferResult, type CaseAssigneesPage, type CaseTransferBody, type CaseTransferResult } from "./caseTransferModel";
import { prepareCaseOperationReceipt, isCaseOperationReceipt, type CaseOperationBody, type CaseOperationReceipt } from "./caseOperationReceiptModel";
import { isCaseQueuePage, type CaseQueueFilters, type CaseQueuePage } from "./caseQueueModel";
import type { User } from "firebase/auth";
import { usesProtectedProcessedWriters } from "../processedWriterMode";
import { isStockSaleStatusSnapshot, isStockSaleStatusResult } from "./stockSaleStatusModel";
import { isStockAffectedOrdersPage, stockAffectedPageMatches } from "./stockAffectedOrdersModel";
import { isOrderPage, orderPageMatches, isOrderDetail, isInventoryHistory, type OrderFilters, type OrderPage, type OrderDetail, type InventoryHistory } from "./orderInspectionModel";
import { isOrderAcceptance, type OrderAcceptance } from "./orderAcceptanceModel";
import { isKustomJobPage, isKustomJobDetail, type JobFilter, type KustomJobPage, type KustomJobDetail } from "./kustomInspectionModel";
import { isMailJobPage, isMailJobDetail, mailPageMatches, type MailFilter, type MailJobPage, type MailJobDetail } from "./mailInspectionModel";
import { isKustomRetryCheck, isKustomRetryResult, type KustomRetryCheck, type KustomRetryResult } from "./kustomInspectionModel";
import { collection, getDocsFromServer, limit, query, where } from "firebase/firestore";
import { firebaseApp } from "../../../components/firebase/firebase";
import { firebaseStore } from "../../../components/firebase/firestore";
import { validShippingProfileDraft, type ShippingProfileDraft } from "./shippingProfileModel";
import { isPackingHistory, type PackingHistoryPage } from "./packingHistoryModel";
import { isPackagePackingHistory, type PackagePackingHistoryPage } from "./packagePackingHistoryModel";
import { validOperatingPolicy, type OperatingPolicyDraft } from "./operatingPolicyModel";
import { isShippingPolicySettings, shippingPolicyResultMatches, type ShippingPolicyBody, type ShippingPolicyResult } from "./shippingPolicyModel";
import { validProcessedProductDraft, validProductId, validProductMedia, type ProcessedProductDraft } from "./processedProductModel";
export type { ProcessedProductDraft } from "./processedProductModel";

export interface ProcessedProductSnapshot {
  documentId: string; businessId: string; expectedDocumentVersion: string; draft: ProcessedProductDraft;
  legacyMedia: { mainImage?: string; imageGallery?: string[] };
}
function isProcessedProductResult(value: unknown): value is PhysicalPackageResult {
  return record(value) && validProductId(value.documentId) && validProductId(value.businessId) && hash(value.operationId);
}
function isProcessedProductSnapshot(value: unknown): value is ProcessedProductSnapshot {
  if (!record(value) || !validProductId(value.documentId) || !validProductId(value.businessId) ||
    typeof value.expectedDocumentVersion !== "string" || !/^\d{1,12}:\d{1,9}$/.test(value.expectedDocumentVersion) ||
    !validProcessedProductDraft(value.draft) || !record(value.legacyMedia)) return false;
  const legacy = value.legacyMedia;
  return Object.keys(legacy).every((key) => ["mainImage", "imageGallery"].includes(key)) &&
    (legacy.mainImage === undefined || validProductMedia(legacy.mainImage)) &&
    (legacy.imageGallery === undefined || (Array.isArray(legacy.imageGallery) && legacy.imageGallery.length <= 20 && legacy.imageGallery.every(validProductMedia)));
}

export interface OperatingSettingsSnapshot {
  exists: boolean; revision: number; draft: OperatingPolicyDraft | null;
  release: { implementationReady: boolean; checkoutRequested: boolean | null; inventoryWritesLocked: boolean | null; effectiveCheckoutEnabled: boolean };
  configuration: { environment: "staging" | "production" | "unconfigured"; kustomEnvironment: "playground" | "production" | null;
    merchantId: string | null; paymentVerification: "not_verified"; shippingVerification: "not_verified"; emailDelivery: ShopEmailConfiguration };
}
export interface OperatingSettingsResult { revision: number; operationId: string; checkoutEnabled: false }
function isOperatingSettings(value: unknown): value is OperatingSettingsSnapshot {
  if (!record(value) || !record(value.release) || !record(value.configuration)) return false;
  const r = value.release, c = value.configuration;
  return typeof value.exists === "boolean" && nonnegativeCount(value.revision) && (value.revision as number) < Number.MAX_SAFE_INTEGER &&
    (value.draft === null || validOperatingPolicy(value.draft)) && typeof r.implementationReady === "boolean" &&
    [r.checkoutRequested, r.inventoryWritesLocked].every((v) => v === null || typeof v === "boolean") &&
    typeof r.effectiveCheckoutEnabled === "boolean" && (!r.effectiveCheckoutEnabled || (r.implementationReady && r.checkoutRequested === true && r.inventoryWritesLocked === true)) &&
    ["staging", "production", "unconfigured"].includes(String(c.environment)) && [null, "playground", "production"].includes(c.kustomEnvironment as any) &&
    (c.merchantId === null || (typeof c.merchantId === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(c.merchantId))) &&
    c.paymentVerification === "not_verified" && c.shippingVerification === "not_verified" && isEmailConfiguration(c.emailDelivery);
}
function isOperatingSettingsResult(value: unknown): value is OperatingSettingsResult {
  return record(value) && nonnegativeCount(value.revision) && (value.revision as number) > 0 && (value.revision as number) <= Number.MAX_SAFE_INTEGER &&
    hash(value.operationId) && value.checkoutEnabled === false;
}

export type ShopCapability = "shop.read" | "shop.catalog.manage" | "shop.inventory.manage" |
  "shop.fulfilment.manage" | "shop.support.manage" | "shop.refunds.manage" | "shop.settings.manage";

export interface ShopMe {
  capabilities: ShopCapability[];
  environment: string;
  projectId: string;
  previewOnly?: boolean;
}

export class ShopApiError extends Error {
  constructor(public status: number, public code: string, public requestId?: string) {
    super(code);
  }
}

export type { ShopOfferSettings } from "./shopOfferModel";

const capabilities: ShopCapability[] = ["shop.read", "shop.catalog.manage", "shop.inventory.manage",
  "shop.fulfilment.manage", "shop.support.manage", "shop.refunds.manage", "shop.settings.manage"];

function apiUrl(path: string): string {
  const origin = process.env.REACT_APP_SHOP_API_ORIGIN?.trim();
  if (!origin) throw new ShopApiError(503, "shop_api_not_configured");
  const url = new URL(`/api/shop/v1/admin/${path}`, origin);
  if (url.protocol !== "http:" && url.protocol !== "https:") throw new ShopApiError(0, "invalid_api_origin");
  return url.toString();
}

async function request<T>(user: User, path: string, validate: (value: unknown) => value is T,
  init?: { method: "PUT" | "POST" | "DELETE"; body: unknown; idempotencyKey?: string; signal?: AbortSignal }): Promise<T> {
  const url = apiUrl(path);
  const token = await user.getIdToken(true);
  if (init?.signal?.aborted) throw new ShopApiError(503, "case_receipt_check_unavailable");
  const response = await fetch(url, {
    method: init?.method || "GET",
    headers: { Authorization: `Bearer ${token}`,
      ...(init ? { "Content-Type": "application/json", ...(init.idempotencyKey ? { "Idempotency-Key": init.idempotencyKey } : {}) } : {}) },
    body: init ? JSON.stringify(init.body) : undefined,
    cache: "no-store",
    ...(init?.signal ? { signal: init.signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const envelope = payload && typeof payload === "object" && "error" in payload ? (payload as { error?: unknown }).error : null;
    const error = envelope && typeof envelope === "object" ? envelope as Record<string, unknown> : {};
    throw new ShopApiError(response.status, typeof error.code === "string" ? error.code : "request_failed",
      typeof error.requestId === "string" ? error.requestId : undefined);
  }
  if (!validate(payload)) throw new ShopApiError(502, "invalid_response");
  return payload;
}

function isShopMe(value: unknown): value is ShopMe {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return Array.isArray(item.capabilities) && item.capabilities.every((entry) => capabilities.includes(entry)) &&
    typeof item.environment === "string" && typeof item.projectId === "string";
}

export interface ShippingProfileSnapshot { profileId: string; revision: number; draft: ShippingProfileDraft }
export interface ShippingProfileResult { profileId: string; revision: number; operationId: string }
export interface PackageShippingSnapshot { packageDocId: string; shippingProfileId: string | null; expectedDocumentVersion: string }
export interface PackageShippingResult { packageDocId: string; shippingProfileId: string | null; revision: number; operationId: string }
function isShippingProfile(value: unknown): value is ShippingProfileSnapshot {
  return record(value) && typeof value.profileId === "string" && nonnegativeCount(value.revision) && (value.revision as number) > 0 &&
    validShippingProfileDraft(value.draft);
}
function isShippingResult(value: unknown): value is ShippingProfileResult {
  return record(value) && typeof value.profileId === "string" && nonnegativeCount(value.revision) && (value.revision as number) > 0 && hash(value.operationId);
}
function isPackageShipping(value: unknown): value is PackageShippingSnapshot {
  return record(value) && typeof value.packageDocId === "string" && (value.shippingProfileId === null || typeof value.shippingProfileId === "string") &&
    typeof value.expectedDocumentVersion === "string" && /^\d{1,12}:\d{1,9}$/.test(value.expectedDocumentVersion);
}
function isPackageShippingResult(value: unknown): value is PackageShippingResult {
  return record(value) && typeof value.packageDocId === "string" && (value.shippingProfileId === null || typeof value.shippingProfileId === "string") &&
    nonnegativeCount(value.revision) && (value.revision as number) > 0 && hash(value.operationId);
}

export interface PhysicalPackageDraft {
  parentId: string; packageType: "bag" | "can" | "box"; productForm: "whole" | "powder" | "slices" | "capsules";
  size: number; formatedSize: string; packageWeight: number | null; material: string;
  packageDimensions: { lengthCm: number; widthCm: number; heightCm: number } | null;
  unitsPerBox: number; boundPackageId: string; active: boolean; ean: string; sku: string;
  priceStore: number; priceRestaurant: number; priceRetail: number; priceOther: number; menuName_sv: string; menuName_en: string;
  origin_sv?: string; origin_en?: string;
  batchLabel?: import("../processedBatchLabels").BatchLabelSettings | null;
}
export interface PhysicalPackageSnapshot {
  documentId: string; businessId: string; expectedDocumentVersion: string; draft: PhysicalPackageDraft;
  shopOffer: { priceWebshop: number | null; vatRate: number | null };
}
export interface PhysicalPackageResult { documentId: string; businessId: string; operationId: string }
function isPhysicalPackageSnapshot(value: unknown): value is PhysicalPackageSnapshot {
  if (!record(value) || !record(value.draft) || !record(value.shopOffer)) return false;
  const draft = value.draft;
  const dimensions = draft.packageDimensions;
  const offer = value.shopOffer;
  const positive = (n: unknown) => typeof n === "number" && Number.isFinite(n) && n > 0;
  const nonnegative = (n: unknown) => typeof n === "number" && Number.isFinite(n) && n >= 0;
  const label = draft.batchLabel;
  const validLabel = label === undefined || label === null || (record(label) &&
    typeof label.widthMm === "number" && Number.isFinite(label.widthMm) && label.widthMm >= 40 &&
    typeof label.heightMm === "number" && Number.isFinite(label.heightMm) && label.heightMm >= 20 &&
    ((label.widthMm <= 105 && label.heightMm <= 148.5) || (label.widthMm <= 148.5 && label.heightMm <= 105)) &&
    [label.backgroundColor, label.accentColor, label.textColor].every((color) => typeof color === "string" && /^#[\da-f]{6}$/i.test(color)));
  return typeof value.documentId === "string" && typeof value.businessId === "string" &&
    validLabel && ["origin_sv", "origin_en"].every((key) => { const origin = draft[key]; return origin === undefined || (typeof origin === "string" && origin.length <= 120); }) &&
    typeof value.expectedDocumentVersion === "string" && /^\d{1,12}:\d{1,9}$/.test(value.expectedDocumentVersion) &&
    ["bag", "can", "box"].includes(String(draft.packageType)) && ["whole", "powder", "slices", "capsules"].includes(String(draft.productForm)) &&
    ["parentId", "formatedSize", "material", "boundPackageId", "ean", "sku", "menuName_sv", "menuName_en"].every((key) => typeof draft[key] === "string") &&
    positive(draft.size) && (draft.packageWeight === null || nonnegative(draft.packageWeight)) &&
    (dimensions === null || (record(dimensions) && ["lengthCm", "widthCm", "heightCm"].every((key) => positive(dimensions[key])))) &&
    nonnegativeCount(draft.unitsPerBox) && typeof draft.active === "boolean" &&
    ["priceStore", "priceRestaurant", "priceRetail", "priceOther"].every((key) => nonnegative(draft[key])) &&
    ["priceWebshop", "vatRate"].every((key) => offer[key] === null || nonnegative(offer[key]));
}
function isPhysicalPackageResult(value: unknown): value is PhysicalPackageResult {
  return record(value) && typeof value.documentId === "string" && typeof value.businessId === "string" && hash(value.operationId);
}

export interface ProcessingBatchDraft {
  process: "freeze_dried" | "dehydrated"; sourceType: "grown_by_us" | "external";
  productId: string; sourceFreshBatchRef: string | null;
  externalProductName: string | null; externalCountry: string | null; externalPurchasedFrom: string | null;
  externalPricePerKg: number | null; freezeDryStart: number; freezeDryEnd: number | null;
  freezeDryBy: string | null; extraInfo: string | null; freshWeight: number | null; dryWeight: number | null; trayCount: number | null;
}
export interface ProcessingBatchSnapshot {
  documentId: string; businessId: string; expectedDocumentVersion: string; draft: ProcessingBatchDraft;
}
export type ProcessingBatchResult = PhysicalPackageResult;
function isProcessingBatchSnapshot(value: unknown): value is ProcessingBatchSnapshot {
  if (!record(value) || !record(value.draft)) return false;
  const draft = value.draft;
  const nonnegative = (n: unknown) => n === null || (typeof n === "number" && Number.isFinite(n) && n >= 0);
  const timestamp = (n: unknown) => Number.isSafeInteger(n) && (n as number) > 0 && (n as number) <= 253402300799999;
  return typeof value.documentId === "string" && typeof value.businessId === "string" &&
    typeof value.expectedDocumentVersion === "string" && /^\d{1,12}:\d{1,9}$/.test(value.expectedDocumentVersion) &&
    ["freeze_dried", "dehydrated"].includes(String(draft.process)) && ["grown_by_us", "external"].includes(String(draft.sourceType)) &&
    typeof draft.productId === "string" && (draft.sourceFreshBatchRef === null || typeof draft.sourceFreshBatchRef === "string") &&
    ["externalProductName", "externalCountry", "externalPurchasedFrom", "freezeDryBy", "extraInfo"].every((key) => draft[key] === null || typeof draft[key] === "string") &&
    ["externalPricePerKg", "freshWeight", "dryWeight", "trayCount"].every((key) => nonnegative(draft[key])) &&
    (draft.trayCount === null || Number.isSafeInteger(draft.trayCount)) && timestamp(draft.freezeDryStart) &&
    (draft.freezeDryEnd === null || (timestamp(draft.freezeDryEnd) && (draft.freezeDryEnd as number) >= (draft.freezeDryStart as number))) &&
    (draft.sourceType === "external" ? draft.productId === "external" && !!draft.externalProductName && draft.sourceFreshBatchRef === null :
      draft.productId !== "external" && ["externalProductName", "externalCountry", "externalPurchasedFrom", "externalPricePerKg"].every((key) => draft[key] === null));
}

export type ShopCatalogKind = "product" | "package" | "processing-batch";
const catalogGroup = (kind: ShopCatalogKind) => kind === "product" ? "products" : kind === "package" ? "packages" : "processing-batches";
export const catalogDeletionReasons = ["writer_migration_required", "ambiguous_id", "invalid_id", "linked_packages", "linked_boxes",
  "linked_stock", "order_history", "reservation_history", "attempt_history", "inventory_history", "history_unresolved", "audit_limit"] as const;
export interface ShopCatalogDeletionCheck {
  kind: ShopCatalogKind; documentId: string; businessId: string | null; expectedDocumentVersion: string;
  canDelete: boolean; blockReasons: typeof catalogDeletionReasons[number][];
}
export interface ShopCatalogDeletionResult { kind: ShopCatalogKind; documentId: string; deleted: true; operationId: string }
function isCatalogDeletionCheck(value: unknown): value is ShopCatalogDeletionCheck {
  return record(value) && ["product", "package", "processing-batch"].includes(String(value.kind)) && typeof value.documentId === "string" &&
    (value.businessId === null || typeof value.businessId === "string") && typeof value.expectedDocumentVersion === "string" &&
    /^\d{1,12}:\d{1,9}$/.test(value.expectedDocumentVersion) && typeof value.canDelete === "boolean" &&
    Array.isArray(value.blockReasons) && value.blockReasons.every((reason: unknown) => catalogDeletionReasons.includes(reason as any)) &&
    value.canDelete === (value.blockReasons.length === 0);
}
function isCatalogDeletionResult(value: unknown): value is ShopCatalogDeletionResult {
  return record(value) && ["product", "package", "processing-batch"].includes(String(value.kind)) && typeof value.documentId === "string" &&
    value.deleted === true && hash(value.operationId);
}

export interface ShopContentChecks {
  foodInformation: boolean; allergens: boolean; nutritionOrExemption: boolean;
  ordinaryFoodAndClaims: boolean; mediaAndTranslations: boolean;
}
export interface ShopProductReadiness {
  productDocId: string; productId: string; isPublic: boolean; productIssues: string[];
  contentIssues: string[]; content: Record<string, unknown>;
  contentReview: { state: "missing" | "invalid" | "hold" | "approved" | "stale";
    revision: number | null; sourceFingerprint: string; fingerprintVersion: string };
}
export interface ShopContentReviewResult {
  productDocId: string; operationId: string;
  review: { status: "approved" | "hold"; revision: number; sourceFingerprint: string; fingerprintVersion: string };
}
const hash = (value: unknown): boolean => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
function isProductReadiness(value: unknown): value is ShopProductReadiness {
  if (!record(value) || !record(value.contentReview)) return false;
  const review = value.contentReview;
  return typeof value.productDocId === "string" && typeof value.productId === "string" && typeof value.isPublic === "boolean" &&
    Array.isArray(value.productIssues) && value.productIssues.every((item: unknown) => typeof item === "string") &&
    Array.isArray(value.contentIssues) && value.contentIssues.every((item: unknown) => typeof item === "string") && record(value.content) &&
    ["missing", "invalid", "hold", "approved", "stale"].includes(String(review.state)) &&
    (review.revision === null || nonnegativeCount(review.revision)) && hash(review.sourceFingerprint) &&
    typeof review.fingerprintVersion === "string";
}
function isContentReviewResult(value: unknown): value is ShopContentReviewResult {
  return record(value) && typeof value.productDocId === "string" && hash(value.operationId) && record(value.review) &&
    ["approved", "hold"].includes(String(value.review.status)) && nonnegativeCount(value.review.revision) &&
    (value.review.revision as number) > 0 && hash(value.review.sourceFingerprint) && typeof value.review.fingerprintVersion === "string";
}

export interface ShopCatalogRebuildPreview {
  mode: "dry_run";
  productId: string;
  currentOffers: number;
  projectedOffers: number;
  offersToDelete: number;
  offersToWrite: number;
  ambiguousProduct: boolean;
}
export interface ShopCatalogRebuildJob { mode: "execute"; jobId: string; state: string }
export interface ShopCatalogRebuildStatus {
  jobId: string;
  state: string;
  kind: string;
  productDocId: string | null;
  summary: Omit<ShopCatalogRebuildPreview, "mode"> | null;
  errorCode: string | null;
}
const nonnegativeCount = (value: unknown): boolean => Number.isSafeInteger(value) && (value as number) >= 0;
function isCatalogSummary(value: unknown): value is Omit<ShopCatalogRebuildPreview, "mode"> {
  return record(value) && typeof value.productId === "string" && nonnegativeCount(value.currentOffers) &&
    nonnegativeCount(value.projectedOffers) && nonnegativeCount(value.offersToDelete) &&
    nonnegativeCount(value.offersToWrite) && typeof value.ambiguousProduct === "boolean";
}
function isCatalogPreview(value: unknown): value is ShopCatalogRebuildPreview {
  return record(value) && value.mode === "dry_run" && isCatalogSummary(value);
}
function isCatalogJob(value: unknown): value is ShopCatalogRebuildJob {
  return record(value) && value.mode === "execute" && typeof value.jobId === "string" && typeof value.state === "string";
}
function isCatalogStatus(value: unknown): value is ShopCatalogRebuildStatus {
  return record(value) && typeof value.jobId === "string" && typeof value.state === "string" &&
    typeof value.kind === "string" && (value.productDocId === null || typeof value.productDocId === "string") &&
    (value.summary === null || isCatalogSummary(value.summary)) &&
    (value.errorCode === null || typeof value.errorCode === "string");
}

export interface ShopStockStatusResult {
  stockDocId: string;
  shopSaleStatus: "approved" | "hold" | "recalled";
  revision: number;
}

export interface ShopStockDraft {
  productId: string;
  packageId: string;
  sourceBatchRefs: string[];
  processDate: string;
  processedBy: string;
  packingDate: string;
  packedBy: string;
  bestBeforeDate: string;
  origin_sv?: string;
  origin_en?: string;
  packagingLanguage?: import("../processedStockModel").PackagingLanguage;
  storageLocation: string;
  notes: string;
  quantity: number;
}

export interface ShopStockWriteResult {
  stockDocId: string;
  quantity: number;
  reservedQuantity: number;
  revision: number;
  shopSaleStatus?: "approved" | "hold" | "recalled";
}

export type ShopAffectedOrders = import("./stockAffectedOrdersModel").StockAffectedOrdersPage;

export interface ShopOverviewData {
  asOf: string;
  supportOpen: number;
  withdrawalsOpen: number;
  heldStock: number;
  paidOrdersAwaitingAction: number | null;
  bookingFailures: number | null;
  refundsRequiringReview: number | null;
}

export type ShopCaseKind = "support" | "withdrawal";
export interface ShopCaseSummary {
  id: string;
  kind: ShopCaseKind;
  state: string;
  receivedAt: string;
  topic?: string;
  assignedToUid: string | null;
  revision: number;
}
export interface ShopCaseDetail extends ShopCaseSummary {
  assignment?: "unassigned" | "current_staff" | "other_staff" | "unknown";
  name: string;
  email: string;
  orderReference: string | null;
  identifyingDetails?: string | null;
  message: string | null;
  goods?: { description: string; quantity: number }[];
  decisions?: { index: number; decision: "eligible" | "ineligible" | "needs_information"; reason: string }[];
  staffNotificationState: string;
  customerReceiptState: string;
  emailDelivery: { configuration: ShopEmailConfiguration; staff: ShopEmailJobSummary; receipt: ShopEmailJobSummary };
}
export type ShopEmailConfiguration = "disabled" | "not_configured" | "configured_unverified";
export interface ShopEmailJobSummary {
  state: "pending" | "leased" | "sending" | "retry" | "blocked_configuration" | "provider_accepted" | "failed" |
    "delivery_unknown" | "quarantined" | "unknown" | "missing";
  attempts: number | null; nextCheckAt: string | null; lastAttemptAt: string | null; acceptedAt: string | null;
  reasonCode: string | null; integrityIssues: string[];
}
const isEmailConfiguration = (value: unknown): value is ShopEmailConfiguration =>
  typeof value === "string" && ["disabled", "not_configured", "configured_unverified"].includes(value);
export function isEmailJobSummary(value: unknown): value is ShopEmailJobSummary {
  if (!record(value)) return false;
  const date = (item: unknown) => item === null || (typeof item === "string" &&
    /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,3})?Z$/.test(item) && Number.isFinite(Date.parse(item)) &&
    new Date(item).toISOString().slice(0, 19) === item.slice(0, 19));
  return Object.keys(value).length === 7 && typeof value.state === "string" && ["pending", "leased", "sending", "retry", "blocked_configuration", "provider_accepted", "failed",
    "delivery_unknown", "quarantined", "unknown", "missing"].includes(value.state) &&
    (value.attempts === null || (Number.isSafeInteger(value.attempts) && (value.attempts as number) >= 0 && (value.attempts as number) <= 8)) &&
    [value.nextCheckAt, value.lastAttemptAt, value.acceptedAt].every(date) &&
    (value.reasonCode === null || (typeof value.reasonCode === "string" && ["invalid_job", "invalid_case", "context_mismatch", "configuration_changed", "source_changed",
      "configuration_unavailable", "smtp_temporary_rejection", "smtp_permanent_rejection", "smtp_outcome_unknown", "sending_lease_expired",
      "retry_exhausted", "unknown"].includes(value.reasonCode))) &&
    Array.isArray(value.integrityIssues) && value.integrityIssues.length <= 7 && new Set(value.integrityIssues).size === value.integrityIssues.length &&
    value.integrityIssues.every((issue) => ["identity", "context", "state", "attempts", "timestamps", "reason", "message"].includes(issue)) &&
    (value.state !== "provider_accepted" || value.integrityIssues.length > 0 ||
      (value.acceptedAt !== null && value.lastAttemptAt !== null && (value.attempts as number) >= 1));
}
export type ShopCasePage = CaseQueuePage;

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const nullableCount = (value: unknown) => value === null || (Number.isSafeInteger(value) && (value as number) >= 0);
function isShopOverview(value: unknown): value is ShopOverviewData {
  if (!record(value)) return false;
  return typeof value.asOf === "string" && Number.isSafeInteger(value.supportOpen) &&
    Number.isSafeInteger(value.withdrawalsOpen) && Number.isSafeInteger(value.heldStock) &&
    nullableCount(value.paidOrdersAwaitingAction) && nullableCount(value.bookingFailures) &&
    nullableCount(value.refundsRequiringReview);
}
function isCaseSummary(value: unknown): value is ShopCaseSummary {
  if (!record(value)) return false;
  return typeof value.id === "string" && (value.kind === "support" || value.kind === "withdrawal") &&
    typeof value.state === "string" && typeof value.receivedAt === "string" &&
    (value.assignedToUid === null || typeof value.assignedToUid === "string") &&
    Number.isSafeInteger(value.revision);
}
function isCaseDetail(value: unknown): value is ShopCaseDetail {
  return isCaseSummary(value) && record(value) && typeof value.name === "string" &&
    (value.assignment === undefined || validCaseAssignment(value.assignment)) &&
    typeof value.email === "string" && (value.orderReference === null || typeof value.orderReference === "string") &&
    (value.message === null || typeof value.message === "string") &&
    typeof value.staffNotificationState === "string" && typeof value.customerReceiptState === "string" &&
    record(value.emailDelivery) && Object.keys(value.emailDelivery).length === 3 && isEmailConfiguration(value.emailDelivery.configuration) &&
    isEmailJobSummary(value.emailDelivery.staff) && isEmailJobSummary(value.emailDelivery.receipt);
}
function isCaseMutation(value: unknown): value is { id: string; state: string; revision: number } {
  return record(value) && typeof value.id === "string" && typeof value.state === "string" && Number.isSafeInteger(value.revision);
}

function isShopStockWriteResult(value: unknown): value is ShopStockWriteResult {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.stockDocId === "string" && Number.isSafeInteger(row.quantity) &&
    Number.isSafeInteger(row.reservedQuantity) && Number.isSafeInteger(row.revision);
}

function isShopStockDeleteResult(value: unknown): value is { stockDocId: string; deleted: true; revision: number } {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.stockDocId === "string" && row.deleted === true && Number.isSafeInteger(row.revision);
}

export const shopStockApiConfigured = usesProtectedProcessedWriters;

export const shopAdminApi = {
  orderAcceptance: (user: User, orderId: string) => request(user, `orders/${encodeURIComponent(orderId)}/acceptance`,
    (value): value is OrderAcceptance => isOrderAcceptance(value) && value.orderId === orderId),
  orders: (user: User, filters: OrderFilters, cursor?: string) => request(user,
    `orders?paymentState=${encodeURIComponent(filters.paymentState)}&mode=${encodeURIComponent(filters.mode)}` +
      `${filters.reservationState && filters.reservationState !== "all" ? `&reservationState=${encodeURIComponent(filters.reservationState)}` : ""}` +
      `${filters.fulfilmentState && filters.fulfilmentState !== "all" ? `&fulfilmentState=${encodeURIComponent(filters.fulfilmentState)}` : ""}` +
      `${filters.createdFrom !== undefined ? `&createdFrom=${encodeURIComponent(filters.createdFrom)}` : ""}` +
      `${filters.createdTo !== undefined ? `&createdTo=${encodeURIComponent(filters.createdTo)}` : ""}` +
      `${filters.reference !== undefined ? `&reference=${encodeURIComponent(filters.reference)}` : ""}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is OrderPage => isOrderPage(value) && orderPageMatches(value, filters, cursor)),
  order: (user: User, orderId: string) => request(user, `orders/${encodeURIComponent(orderId)}`,
    (value): value is OrderDetail => isOrderDetail(value) && value.order.orderId === orderId),
  inventoryHistory: (user: User, orderId: string, cursor?: string) => request(user,
    `orders/${encodeURIComponent(orderId)}/inventory-history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is InventoryHistory => isInventoryHistory(value) && value.orderId === orderId),
  mailJobs: (user: User, state: MailFilter = "attention", cursor?: string) =>
    request(user, `mail-jobs?state=${encodeURIComponent(state)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      (value): value is MailJobPage => isMailJobPage(value) && mailPageMatches(value, state)),
  mailJob: (user: User, jobId: string) => request(user, `mail-jobs/${encodeURIComponent(jobId)}`,
    (value): value is MailJobDetail => isMailJobDetail(value) && value.job.jobId === jobId),
  kustomJobs: (user: User, state: JobFilter = "quarantined", cursor?: string) =>
    request(user, `kustom-jobs?state=${encodeURIComponent(state)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      (value): value is KustomJobPage => isKustomJobPage(value) && (state === "all" || value.jobs.every((job) => job.state === state))),
  kustomJob: (user: User, jobId: string) => request(user, `kustom-jobs/${encodeURIComponent(jobId)}`,
    (value): value is KustomJobDetail => isKustomJobDetail(value) && value.job.jobId === jobId),
  kustomRetryCheck: (user: User, jobId: string) => request(user, `kustom-jobs/${encodeURIComponent(jobId)}/retry-check`,
    (value): value is KustomRetryCheck => isKustomRetryCheck(value) && value.jobId === jobId),
  requestKustomRetry: (user: User, jobId: string, body: { expectedDocumentVersion: string; reason: string }, key: string) =>
    request(user, `kustom-jobs/${encodeURIComponent(jobId)}/retry`,
      (value): value is KustomRetryResult => isKustomRetryResult(value) && value.jobId === jobId,
      { method: "POST", body, idempotencyKey: key }),
  processedProduct: (user: User, documentId: string) => request(user, `products/${encodeURIComponent(documentId)}/catalog`,
    (value): value is ProcessedProductSnapshot => isProcessedProductSnapshot(value) && value.documentId === documentId),
  createProcessedProduct: (user: User, body: { draft: ProcessedProductDraft; reason: string }, key: string) =>
    request(user, "products", isProcessedProductResult, { method: "POST", body, idempotencyKey: key }),
  updateProcessedProduct: (user: User, documentId: string, body: { draft: ProcessedProductDraft; reason: string; expectedDocumentVersion: string }, key: string) =>
    request(user, `products/${encodeURIComponent(documentId)}/catalog`,
      (value): value is PhysicalPackageResult => isProcessedProductResult(value) && value.documentId === documentId,
      { method: "PUT", body, idempotencyKey: key }),
  settingsStatus: (user: User) => request(user, "settings/status", isOperatingSettings),
  policyHistory: (user: User, cursor?: string) => request(user, `settings/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, isPolicyHistory),
  settings: (user: User) => request(user, "settings", isOperatingSettings),
  updateSettings: (user: User, body: { draft: OperatingPolicyDraft; expectedRevision: number; reason: string }, key: string) =>
    request(user, "settings", isOperatingSettingsResult, { method: "PUT", body, idempotencyKey: key }),
  closeCheckout: (user: User, body: { expectedRevision: number; reason: string }, key: string) =>
    request(user, "settings/close-checkout", isOperatingSettingsResult, { method: "POST", body, idempotencyKey: key }),
  shippingProfile: (user: User, profileId: string) => request(user, `shipping-profiles/${encodeURIComponent(profileId)}`,
    (value): value is ShippingProfileSnapshot => isShippingProfile(value) && value.profileId === profileId),
  shippingPolicy: (user: User) => request(user, "shipping-policy", isShippingPolicySettings),
  updateShippingPolicy: (user: User, body: ShippingPolicyBody, key: string) => request(user, "shipping-policy",
    (value): value is ShippingPolicyResult => shippingPolicyResultMatches(value, body), { method: "PUT", body, idempotencyKey: key }),
  packingHistory: (user: User, profileId: string, cursor?: string) => request(user,
    `shipping-profiles/${encodeURIComponent(profileId)}/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is PackingHistoryPage => isPackingHistory(value) && value.profileId === profileId),
  createShippingProfile: (user: User, body: { draft: ShippingProfileDraft; reason: string }, key: string) =>
    request(user, "shipping-profiles", isShippingResult, { method: "POST", body, idempotencyKey: key }),
  updateShippingProfile: (user: User, profileId: string, body: { draft: ShippingProfileDraft; reason: string; expectedRevision: number }, key: string) =>
    request(user, `shipping-profiles/${encodeURIComponent(profileId)}`,
      (value): value is ShippingProfileResult => isShippingResult(value) && value.profileId === profileId,
      { method: "PUT", body, idempotencyKey: key }),
  packageShippingProfile: (user: User, packageDocId: string) => request(user, `packages/${encodeURIComponent(packageDocId)}/shipping-profile`,
    (value): value is PackageShippingSnapshot => isPackageShipping(value) && value.packageDocId === packageDocId),
  packagePackingHistory: (user: User, packageDocId: string, cursor?: string) => request(user,
    `packages/${encodeURIComponent(packageDocId)}/shipping-profile/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is PackagePackingHistoryPage => isPackagePackingHistory(value) && value.packageDocId === packageDocId),
  assignShippingProfile: (user: User, packageDocId: string, body: { shippingProfileId: string | null; reason: string; expectedDocumentVersion: string }, key: string) =>
    request(user, `packages/${encodeURIComponent(packageDocId)}/shipping-profile`,
      (value): value is PackageShippingResult => isPackageShippingResult(value) && value.packageDocId === packageDocId,
      { method: "PUT", body, idempotencyKey: key }),
  processingBatch: (user: User, documentId: string) => request(user, `processing-batches/${encodeURIComponent(documentId)}`,
    (value): value is ProcessingBatchSnapshot => isProcessingBatchSnapshot(value) && value.documentId === documentId),
  createProcessingBatch: (user: User, body: { draft: ProcessingBatchDraft; reason: string }, key: string) =>
    request(user, "processing-batches", isPhysicalPackageResult, { method: "POST", body, idempotencyKey: key }),
  updateProcessingBatch: (user: User, documentId: string,
    body: { draft: ProcessingBatchDraft; reason: string; expectedDocumentVersion: string }, key: string) =>
    request(user, `processing-batches/${encodeURIComponent(documentId)}`,
      (value): value is ProcessingBatchResult => isPhysicalPackageResult(value) && value.documentId === documentId,
      { method: "PUT", body, idempotencyKey: key }),
  physicalPackage: (user: User, documentId: string) => request(user, `packages/${encodeURIComponent(documentId)}/catalog`,
    (value): value is PhysicalPackageSnapshot => isPhysicalPackageSnapshot(value) && value.documentId === documentId),
  createPhysicalPackage: (user: User, body: { draft: PhysicalPackageDraft; reason: string }, key: string) =>
    request(user, "packages", isPhysicalPackageResult, { method: "POST", body, idempotencyKey: key }),
  updatePhysicalPackage: (user: User, documentId: string,
    body: { draft: PhysicalPackageDraft; reason: string; expectedDocumentVersion: string }, key: string) =>
    request(user, `packages/${encodeURIComponent(documentId)}/catalog`,
      (value): value is PhysicalPackageResult => isPhysicalPackageResult(value) && value.documentId === documentId,
      { method: "PUT", body, idempotencyKey: key }),
  me: async (user: User): Promise<ShopMe> => {
    try { return await request(user, "me", isShopMe); }
    catch (error) {
      // Before shopAdminApi is deployed, the existing dashboard can still
      // show canonical catalogue data under its current Firestore owner rules.
      // Never replace an explicit API 401/403 with a browser-only decision.
      if (error instanceof ShopApiError && ![0, 404, 502, 503].includes(error.status)) throw error;
      if (!user.email) throw new ShopApiError(403, "forbidden");
      const token = await user.getIdTokenResult(true);
      if (token.claims.role !== "owner") throw new ShopApiError(403, "forbidden");
      const roles = await getDocsFromServer(query(collection(firebaseStore, "authUsers"),
        where("email", "==", user.email), limit(2)));
      if (roles.size !== 1 || roles.docs[0].data().role !== "owner") throw new ShopApiError(403, "forbidden");
      return { capabilities: ["shop.read"], environment: "Förhandsvisning, endast läsning",
        projectId: firebaseApp.options.projectId || "okänt", previewOnly: true };
    }
  },
  productReadiness: (user: User, productDocId: string) =>
    request(user, `products/${encodeURIComponent(productDocId)}/shop-readiness`, isProductReadiness),
  catalogDeletionCheck: (user: User, kind: ShopCatalogKind, documentId: string) =>
    request(user, `${catalogGroup(kind)}/${encodeURIComponent(documentId)}/deletion-check`,
      (value): value is ShopCatalogDeletionCheck => isCatalogDeletionCheck(value) && value.kind === kind && value.documentId === documentId),
  deleteCatalogRecord: (user: User, kind: ShopCatalogKind, documentId: string,
    body: { expectedDocumentVersion: string; reason: string }, key: string) =>
    request(user, `${catalogGroup(kind)}/${encodeURIComponent(documentId)}`,
      (value): value is ShopCatalogDeletionResult => isCatalogDeletionResult(value) && value.kind === kind && value.documentId === documentId,
      { method: "DELETE", body, idempotencyKey: key }),
  reviewContent: (user: User, productDocId: string, body: { expectedRevision: number; sourceFingerprint: string;
    status: "approved" | "hold"; reason: string; checks: ShopContentChecks }, key: string) =>
    request(user, `products/${encodeURIComponent(productDocId)}/shop-content-review`, isContentReviewResult,
      { method: "POST", body, idempotencyKey: key }),
  offerHistory: (user: User, packageDocId: string, cursor?: string) => request(user,
    `packages/${encodeURIComponent(packageDocId)}/shop-offer/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is OfferHistoryPage => isOfferHistory(value) && value.packageDocId === packageDocId &&
      (cursor === undefined || (offerHistoryCursorLast(cursor, packageDocId) !== null &&
        value.operations.every((row) => row.operationId > offerHistoryCursorLast(cursor, packageDocId)!)))),
  offer: (user: User, packageDocId: string) => request(user, `packages/${encodeURIComponent(packageDocId)}/shop-offer`,
    (value): value is ShopOfferSettings => isShopOfferSettings(value) && value.packageDocId === packageDocId),
  updateOffer: (user: User, packageDocId: string, settings: ShopOfferBody, key: string) =>
    request(user, `packages/${encodeURIComponent(packageDocId)}/shop-offer`,
      (value): value is ShopOfferSettings => isShopOfferSettings(value) && shopOfferResultMatches(value, packageDocId, settings),
      { method: "PUT", body: settings, idempotencyKey: key }),
  previewCatalogRebuild: (user: User, productDocId: string, reason: string) =>
    request(user, "catalog/rebuild", isCatalogPreview, { method: "POST",
      body: { productDocId, mode: "dry_run", reason }, idempotencyKey: crypto.randomUUID() }),
  executeCatalogRebuild: (user: User, productDocId: string, reason: string, key: string) =>
    request(user, "catalog/rebuild", isCatalogJob, { method: "POST",
      body: { productDocId, mode: "execute", reason }, idempotencyKey: key }),
  catalogRebuildStatus: (user: User, jobId: string) =>
    request(user, `catalog/rebuild/${encodeURIComponent(jobId)}`, isCatalogStatus),
  stockSaleStatus: (user: User, stockDocId: string) => request(user, `stock/${encodeURIComponent(stockDocId)}/sale-status`,
    (value): value is import("./stockSaleStatusModel").StockSaleStatusSnapshot => isStockSaleStatusSnapshot(value) && value.stockDocId === stockDocId),
  updateStockSaleStatus: (user: User, stockDocId: string, settings: {
    expectedRevision: number; shopSaleStatus: "approved" | "hold" | "recalled"; reason: string;
  }, key: string) => request(user, `stock/${encodeURIComponent(stockDocId)}/sale-status`,
    (value): value is ShopStockStatusResult => isStockSaleStatusResult(value) && value.stockDocId === stockDocId &&
      value.shopSaleStatus === settings.shopSaleStatus && value.revision === settings.expectedRevision + 1,
    { method: "POST", body: settings, idempotencyKey: key }),
  createStock: (user: User, draft: ShopStockDraft, key: string) =>
    request(user, "stock", isShopStockWriteResult, { method: "POST", body: draft, idempotencyKey: key }),
  updateStock: (user: User, stockDocId: string, draft: ShopStockDraft & { expectedRevision: number; reason: string }, key: string) =>
    request(user, `stock/${encodeURIComponent(stockDocId)}`, isShopStockWriteResult,
      { method: "PUT", body: draft, idempotencyKey: key }),
  deleteStock: (user: User, stockDocId: string, input: { expectedRevision: number; reason: string }, key: string) =>
    request(user, `stock/${encodeURIComponent(stockDocId)}`, isShopStockDeleteResult,
      { method: "DELETE", body: input, idempotencyKey: key }),
  affectedOrders: (user: User, stockDocId: string, cursor?: string) =>
    request(user, `stock/${encodeURIComponent(stockDocId)}/affected-orders${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      (value): value is ShopAffectedOrders => isStockAffectedOrdersPage(value) && stockAffectedPageMatches(value, stockDocId, cursor)),
  overview: (user: User) => request(user, "overview", isShopOverview),
  cases: (user: User, kind: ShopCaseKind, filters: CaseQueueFilters = { state: "all", assignment: "all" }, cursor?: string) => {
    const query = new URLSearchParams({ state: filters.state, assignment: filters.assignment });
    if (cursor) query.set("cursor", cursor);
    return request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}?${query}`,
      (value): value is CaseQueuePage => isCaseQueuePage(value) && value.kind === kind &&
        value.filters.state === filters.state && value.filters.assignment === filters.assignment);
  },
  caseDetail: (user: User, kind: ShopCaseKind, id: string) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}`,
      (value): value is ShopCaseDetail => isCaseDetail(value) && value.id === id && value.kind === kind),
  caseOperationReceipt: async (user: User, kind: ShopCaseKind, id: string, body: CaseOperationBody, key: string) => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const deadline = new Promise<never>((_resolve, reject) => { timer = setTimeout(() => {
      controller.abort(); reject(new ShopApiError(503, "case_receipt_check_unavailable"));
    }, 15_000); });
    const work = async () => {
      let prepared: Awaited<ReturnType<typeof prepareCaseOperationReceipt>>;
      try { prepared = await prepareCaseOperationReceipt({ actorUid: user.uid, kind, caseId: id, key, body }); }
      catch { throw new ShopApiError(503, "case_receipt_proof_unavailable"); }
      if (controller.signal.aborted) throw new ShopApiError(503, "case_receipt_check_unavailable");
      return request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/operation-receipt`,
        (value): value is CaseOperationReceipt => isCaseOperationReceipt(value, prepared.context), { method: "POST", body: prepared.query, signal: controller.signal });
    };
    try { return await Promise.race([work(), deadline]); }
    catch (caught) { if (caught instanceof ShopApiError) throw caught; throw new ShopApiError(503, "case_receipt_check_unavailable"); }
    finally { clearTimeout(timer!); }
  },
  caseHistory: (user: User, kind: ShopCaseKind, id: string, cursor?: string) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      (value): value is import("./caseHistoryModel").CaseHistoryPage => isCaseHistoryPage(value) && value.kind === kind && value.caseId === id),
  caseOrderCheck: (user: User, kind: ShopCaseKind, id: string, reference: string, expectedRevision: number) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/order-check?${new URLSearchParams({ reference, expectedRevision: String(expectedRevision) })}`,
      (value): value is CaseOrderCheck => isCaseOrderCheck(value) && value.kind === kind && value.caseId === id &&
        value.reference === reference && value.currentRevision === expectedRevision),
  caseAssignmentHistory: (user: User, kind: ShopCaseKind, id: string, cursor?: string) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/assignment-history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      (value): value is import("./caseAssignmentModel").CaseAssignmentHistoryPage => isCaseAssignmentHistoryPage(value) && value.kind === kind && value.caseId === id),
  caseAssignees: (user: User, cursor?: string) => request(user, `case-assignees${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    (value): value is CaseAssigneesPage => isCaseAssigneesPage(value) && value.staff.every((row) => row.uid !== user.uid)),
  transferCase: (user: User, kind: ShopCaseKind, id: string, body: CaseTransferBody, key: string) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/transfer`,
      (value): value is CaseTransferResult => isCaseTransferResult(value) && value.kind === kind && value.caseId === id && value.revision === body.expectedRevision + 1,
      { method: "POST", body, idempotencyKey: key }),
  assignCase: (user: User, kind: ShopCaseKind, id: string, body: CaseAssignmentBody, key: string) =>
    request(user, `${kind === "support" ? "support-requests" : "withdrawal-requests"}/${encodeURIComponent(id)}/assignment`,
      (value): value is import("./caseAssignmentModel").CaseAssignmentResult => isCaseAssignmentResult(value) && value.kind === kind && value.caseId === id &&
        value.assignment === body.assignment && value.revision === body.expectedRevision + 1, { method: "POST", body, idempotencyKey: key }),
  triageSupport: (user: User, id: string, body: { expectedRevision: number; state: "triaged" | "resolved"; reason: string }, key: string) =>
    request(user, `support-requests/${encodeURIComponent(id)}/triage`,
      (value): value is { id: string; state: string; revision: number } => isCaseMutation(value) && value.id === id &&
        value.state === body.state && value.revision === body.expectedRevision + 1,
      { method: "POST", body, idempotencyKey: key }),
  reviewWithdrawal: (user: User, id: string, body: { expectedRevision: number; reason: string;
    items: { index: number; decision: "eligible" | "ineligible" | "needs_information"; reason: string }[] }, key: string) =>
    request(user, `withdrawal-requests/${encodeURIComponent(id)}/review`,
      (value): value is { id: string; state: string; revision: number } => isCaseMutation(value) && value.id === id &&
        value.state === (body.items.some((item) => item.decision === "needs_information") ? "under_review" : "reviewed") && value.revision === body.expectedRevision + 1,
      { method: "POST", body, idempotencyKey: key }),
};
